import React, {useEffect, useState} from "react";
import {
    Alert,
    Avatar,
    Button,
    Container,
    DialogActions,
    Grid,
    Snackbar,
    TextField,
    Typography,
    Box,
    Paper,
    IconButton,
    Chip,
    Stack,
} from "@mui/material";
import {styled} from "@mui/material/styles";
import dayjs from "dayjs";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

// Styled Components
const StyledPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3),
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const UploadBox = styled(Box)(({theme}) => ({
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.dark,
    },
}));

const ImagePreview = styled(Box)(({theme}) => ({
    position: 'relative',
    width: '100%',
    maxWidth: 200,
    margin: '16px auto',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
}));

const SchoolAssetForm = ({Assets, onCancel, onSubmit, asset}) => {
    const [formData, setFormData] = useState({
        type: "",
        staffName: "",
        description: "",
        quantity: "",
        creationDateTime: null,
        assetPhoto: null,
    });

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [fileName, setFileName] = useState("");
    useEffect(() => {
        if (Assets) {
            setFormData({
                ...Assets,
                creationDateTime: Assets.creationDateTime
                    ? dayjs(Assets.creationDateTime)
                    : null,
            });
        }
    }, [Assets]);


    useEffect(() => {
        if (asset) {
            setFormData({
                id: asset.id || null,
                assetName: asset.assetName || '',
                type: asset.type || '',
                quantity: asset.quantity || '',
                description: asset.description || '',
                assetPhoto: asset.assetPhoto || null,
                creationDateTime: asset.creationDateTime ? dayjs(asset.creationDateTime) : null,
            });
            if (asset.assetName) {
                setFileName(asset.assetName);
            }
        }
    }, [asset]);


    const handleChange = async (e) => {
        const {name, value, files} = e.target;

        if (files) {
            const file = files[0];
            const base64 = await convertToBase64(file);
            setFormData({
                ...formData,
                [name]: base64
            });
        } else {
            //   setFormData({
            //     ...formData,
            //     [name]: value
            // });
            setFormData((prev) => ({...prev, [name]: value}));
        }
// Validate the changed field
        const fieldError = validateForm(name, value);
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: fieldError
        }));
    }

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = error => reject(error);
        });
    };


    const validateForm = () => {
        const newErrors = {};
        if (!formData.type) newErrors.type = "Asset type is required";
        if (!formData.assetName) newErrors.assetName = "Asset name is required";
        if (!formData.quantity || isNaN(formData.quantity))
            newErrors.quantity = "Quantity must be a number";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // const handleSubmit = () => {
    //   const assetData = {
    //     ...formData,
    //     id: asset?.id || null, // Include id for updates; null for new asset
    //   };
    //   onSubmit(assetData);
    // };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // const data = { ...formData };
        onSubmit(formData);
        console.log(formData)
        // handleSave(formData);
        setSuccessMessage("Asset information submitted successfully!");

        setFormData({
            type: "",
            assetName: "",


            description: "",

            quantity: "",
            creationDateTime: null,
            assetPhoto: null,
        });

        setTimeout(() => {
            setSuccessMessage("");
        }, 3000);
    };

    const handleRemoveImage = () => {
        setFormData({...formData, assetPhoto: null});
        setFileName("");
    };

    const getImageSrc = (photo) => {
        if (!photo) return '';
        if (typeof photo === 'string' && photo.startsWith('data:image')) {
            return photo;
        }
        return `data:image/jpeg;base64,${photo}`;
    };

    const renderImageUpload = (label, name) => (
        <Grid item xs={12} key={name}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                {label}
            </Typography>
            {!formData[name] ? (
                <UploadBox>
                    <input
                        accept="image/*"
                        style={{display: 'none'}}
                        id={`upload-${name}`}
                        type="file"
                        name={name}
                        onChange={handleChange}
                    />
                    <label htmlFor={`upload-${name}`}>
                        <Stack spacing={2} alignItems="center">
                            <PhotoCameraIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                            <Typography variant="body1" color="text.secondary">
                                Click to upload asset photo
                            </Typography>
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                                sx={{ borderRadius: 2 }}
                            >
                                Choose File
                            </Button>
                        </Stack>
                    </label>
                </UploadBox>
            ) : (
                <ImagePreview>
                    <img
                        src={getImageSrc(formData[name])}
                        alt={label}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'error.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'error.dark',
                            },
                        }}
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </ImagePreview>
            )}
        </Grid>
    );
    return (
        <Container maxWidth="md">
            <StyledPaper elevation={0}>
                <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" gutterBottom fontWeight={700}>
                            {asset?.id ? 'Edit Asset' : 'Add New Asset'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {asset?.id ? 'Update the asset information below' : 'Fill in the details to add a new asset'}
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Asset Type */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Asset Type"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                error={!!errors.type}
                                helperText={errors.type}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>

                        {/* Asset Name */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Asset Name"
                                name="assetName"
                                value={formData.assetName}
                                onChange={handleChange}
                                error={!!errors.assetName}
                                helperText={errors.assetName}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>

                        {/* Quantity */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Quantity"
                                name="quantity"
                                type="number"
                                value={formData.quantity}
                                onChange={handleChange}
                                error={!!errors.quantity}
                                helperText={errors.quantity}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder="Enter asset description (optional)"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    }
                                }}
                            />
                        </Grid>

                        {/* Asset Photo */}
                        {renderImageUpload("Asset Photo", "assetPhoto")}
                    </Grid>

                    {/* Action Buttons */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={onCancel}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{
                                borderRadius: 2,
                                px: 4,
                            }}
                        >
                            {asset?.id ? "Update Asset" : "Add Asset"}
                        </Button>
                    </Box>
                </form>
            </StyledPaper>

            <Snackbar
                open={!!successMessage}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage("")}
                anchorOrigin={{vertical: "top", horizontal: "center"}}
            >
                <Alert
                    onClose={() => setSuccessMessage("")}
                    severity="success"
                    variant="filled"
                    sx={{
                        width: "100%",
                        borderRadius: 2,
                        fontWeight: 600,
                    }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default SchoolAssetForm;

